import React, { useCallback, useEffect, useId, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { closeModal, selectActiveModalData } from 'features/modal/modalSlice';
import { Trans, useTranslation } from 'react-i18next';
import { useBreakoutRooms } from 'features/breakout-rooms/useBreakoutRooms';
import {
  selectActiveBreakoutUser,
  selectBreakoutParticipantsLimit,
  selectBreakoutRoomsCreated,
} from 'features/breakout-rooms/breakoutRoomsSlice';
import TargetRoomSelector, {
  SelectedRoomId,
} from 'features/breakout-rooms/components/target-room-selector/TargetRoomSelector';
import { Box } from '@mui/material';
import { assignUsersToBreakoutRoomRequested } from 'features/breakout-rooms/actions';
import Button from '@mui/material/Button';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from 'components/Dialog';

const AssignUserToRoomModal = () => {
  const { t } = useTranslation('modals');
  const dispatch = useAppDispatch();

  const modalId = useId();

  const modalData = useAppSelector(selectActiveModalData);
  const userId = modalData.userId as string;

  const user = useAppSelector((state) => selectActiveBreakoutUser(state, userId));

  const [selectedId, setSelectedId] = useState<SelectedRoomId>();

  const { hasBreakoutManagementAccess } = useBreakoutRooms();

  const participantsLimit = useAppSelector(selectBreakoutParticipantsLimit);
  const breakoutRoomsCreated = useAppSelector(selectBreakoutRoomsCreated);

  const modalShouldBeClosed = !hasBreakoutManagementAccess || !breakoutRoomsCreated || !user;

  useEffect(() => {
    if (modalShouldBeClosed) {
      dispatch(closeModal('assignUserToBreakoutRoom'));
    }
  }, [modalShouldBeClosed, dispatch]);

  const handleCancel = () => {
    dispatch(closeModal('assignUserToBreakoutRoom'));
  };

  const handleOk = () => {
    dispatch(
      assignUsersToBreakoutRoomRequested({
        id: selectedId as string,
        users: [userId],
      })
    );
    dispatch(closeModal('assignUserToBreakoutRoom'));
  };

  const handleRoomSelect = useCallback((roomId: string) => {
    setSelectedId(roomId);
  }, []);

  return (
    <Dialog
      open={!modalShouldBeClosed}
      maxWidth="xs"
      aria-labelledby={`${modalId}-title`}
      aria-describedby={`${modalId}-description`}
    >
      <DialogTitle id={`${modalId}-title`}>
        {t('breakout_rooms.assign_single_participant.title')}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id={`${modalId}-description`}>
          <Trans
            i18nKey="modals:breakout_rooms.assign_single_participant.description"
            t={t}
            values={{
              username: user?.name,
              participantsLimit,
            }}
            components={{
              bold: <strong />,
            }}
          />
        </DialogContentText>
        <Box
          sx={{
            mt: 6,
          }}
        >
          <TargetRoomSelector
            selectedId={selectedId}
            selectRoom={handleRoomSelect}
            participantsLimit={participantsLimit}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color="grayscale">
          {t('breakout_rooms.assign_single_participant.btn_cancel_label')}
        </Button>
        <Button onClick={handleOk} disabled={!selectedId}>
          {t('breakout_rooms.assign_single_participant.btn_ok_label')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignUserToRoomModal;
